import { constants, useBreakpointRange, useKeyUp } from "@newsela/angelou";
import { actionNames, objectTypes } from "@newsela/snitch";
import { useRouter } from "next/router";
import { useRef } from "react";
import FocusLock from "react-focus-lock";
import { FocusOn } from "react-focus-on";

import { ATTACHMENT_CONTENT_TYPE } from "@/types/models";
import useGetUserProperties from "@/users/v3/useGetUserProperties";
import ActivityHeader from "@/view/ActivityHeader/ActivityHeader";
import {
  containerCss,
  focusContainerCss,
  wrapperCss,
} from "@/view/ActivityPanel/ActivityPanel.style";
import ActivityPanelTab from "@/view/ActivityPanel/ActivityPanelTab";
import UnlevelledContentDiscussionContainer from "@/view/UnlevelledContentDiscussionContainer";
import UnlevelledContentTeacherResourcesContainer from "@/view/UnlevelledContentTeacherResourcesContainer/UnlevelledContentTeacherResourcesContainer";
import UnlevelledContentVocabularyContainer from "@/view/UnlevelledContentVocabularyContainer";
import PanelHeader from "@/view/PanelHeader";
import useActivityPanel from "@/view/utils/useActivityPanel";
import useAttachmentsContext from "@/view/utils/useAttachmentsContext";
import { Assignment } from "@/types/models/Assignment.interface";
import UnlevelledContentAssignmentContainer from "@/view/UnlevelledContent/UnlevelledContentAssignmentContainer";
import { VideoSubject } from "@/types/models/UnlevelledContent.interface";
import { TGeniusPanelName } from "./UnlevelledContentActivityPanel.constant";
import {
  activityListCss,
  listItemCss,
  panelCss,
  panelItemWrapperCss,
  sectionCss,
  sectionTextCss,
  sectionTitleCss,
} from "./UnlevelledContentActivityPanel.style";
import UnlevelledContentActivityPanelOption from "./UnlevelledContentActivityPanelOption";
import LearnMoreFormativeLink from "./LearnMoreFormativeLink";
import {
  getActivitiesFromContentData,
  getBackButtonHandler,
  getClickHandler,
} from "./utils";
import UnlevelledContentAssignmentOption from "./UnlevelledContentAssignmentOption";
import { getSortedFormativeAssessments } from "./utils";
import type { GenerationGeniusAPIResponse } from "@/types/models/GenerationGenius.interface";
import type {
  IUnlevelledContent,
  GeniusAttachmentResponse,
} from "@/types/models/UnlevelledContent.interface";

export interface UnlevelledContentActivityPanelProps {
  assignments: Assignment[];
  assignmentsLoading: boolean;
  content: IUnlevelledContent;
}

// An array of assessment names we expect to see, in the order we want to display them.
export const EXPECTED_ASSESSMENT_LINKS = [
  "Practice Word Problems",
  "Practice Number Problems",
  "Exit Ticket",
  "Genius Challenge",
  "Quiz Game",
];

const { zeroToXs, mdToLg, lgToInfinity } =
  constants.breakpoints.BreakpointRanges;

function getPanelContent(
  activityPanelKey: string | undefined,
  sharedContainerProps: any,
  renderActivityAndAttachmentList: () => React.ReactNode,
  assignments: Assignment[],
  assignmentsLoading: boolean,
  subject: string,
) {
  switch (activityPanelKey) {
    case "vocabulary":
      return <UnlevelledContentVocabularyContainer {...sharedContainerProps} />;
    case "discussion_questions":
      return <UnlevelledContentDiscussionContainer {...sharedContainerProps} />;
    case "teacher_resources":
      return (
        <UnlevelledContentTeacherResourcesContainer
          {...sharedContainerProps}
          subject={subject}
        />
      );
    case "assignment":
      return (
        <UnlevelledContentAssignmentContainer
          {...sharedContainerProps}
          assignments={assignments}
          assignmentsLoading={assignmentsLoading}
        />
      );
    default:
      return renderActivityAndAttachmentList();
  }
}

const UnlevelledContentActivityPanel = ({
  assignments = [],
  assignmentsLoading = false,
  content,
}: UnlevelledContentActivityPanelProps) => {
  const router = useRouter();
  const shouldFocusCloseButtonRef = useRef(false);
  const { isTeacher, isStudent } = useGetUserProperties() || {};
  const onClickBackButton = getBackButtonHandler(router);

  const isArticle = false;

  const { isActivityPanelExpanded, setIsActivityPanelExpanded } =
    useActivityPanel();

  const breakpointRange = useBreakpointRange();
  const isSmallerScreen =
    breakpointRange !== mdToLg && breakpointRange !== lgToInfinity;
  const isSmallestScreen = breakpointRange === zeroToXs;
  const { attachments } = useAttachmentsContext() as GeniusAttachmentResponse;
  const filteredAttachments = attachments?.filter(
    ({ content_type }) => content_type === ATTACHMENT_CONTENT_TYPE.FORMATIVE,
  );

  const { query } = router;
  const { levelId = "" } = query;
  const activityPanel = query["activityPanel"] as TGeniusPanelName;

  const backButtonEventProperties = {
    objectType: objectTypes.content,
    actionName: actionNames.navigate,
    streamProperties: {
      content_type: "GenerationGeniusVideo",
      description: `Navigate back to Activity Picker from Generation Genius ${activityPanel}`,
      content_id: typeof levelId === "string" ? levelId : levelId[0] || "",
    },
  };

  const closeButtonEventProperties = {
    objectType: objectTypes.content,
    actionName: actionNames.hide,
    streamProperties: {
      content_type: "GenerationGeniusVideo",
      description: `Close Activity Picker from Generation Genius ${activityPanel}`,
      content_id: typeof levelId === "string" ? levelId : levelId[0] || "",
    },
  };

  const onClickCloseButton = () => {
    setIsActivityPanelExpanded(false);
  };

  const onEsc = () => {
    if (isSmallerScreen) {
      setIsActivityPanelExpanded(false);
    }
  };

  useKeyUp("escape", onEsc, {
    dependencyArray: [breakpointRange],
  });

  const contentData = content.data as GenerationGeniusAPIResponse;

  const availableActivities = getActivitiesFromContentData({
    contentData,
    isTeacher: Boolean(isTeacher),
  });
  const handleClick = getClickHandler({
    router,
    contentData,
  });

  const sharedContainerProps = {
    shouldFocusCloseButtonRef,
    contentData,
    onClickBackButton,
    backButtonEventProperties,
    closeButtonEventProperties,
    onClickCloseButton,
  };

  const renderAssignmentsSection = () => {
    if (!isStudent || assignments.length === 0) {
      return null;
    }

    return (
      <div css={panelItemWrapperCss}>
        <h3 css={sectionTitleCss}>Assignments</h3>
        <ul css={activityListCss}>
          {assignments &&
            assignments.map((assignment, index) => {
              return (
                <li css={listItemCss} key={index}>
                  <UnlevelledContentAssignmentOption
                    key={index}
                    assignment={assignment}
                    onClick={() =>
                      handleClick({
                        type: "internal-panel-link",
                        activityPanel: "assignment",
                        assignmentId:
                          assignment?.assignment_id?.toString() || "",
                      })
                    }
                    content={content}
                  />
                </li>
              );
            })}
        </ul>
      </div>
    );
  };

  const renderActivitiesSection = () => {
    if (availableActivities.length === 0) {
      return <h3 css={sectionTitleCss}>No Lesson Materials Available</h3>;
    }
    return (
      <div css={panelItemWrapperCss}>
        <h3 css={sectionTitleCss}>Lesson Materials</h3>
        <ul role="group" css={activityListCss}>
          {availableActivities.map(
            ({ activityPanel: panel, title, type, url }) => (
              <UnlevelledContentActivityPanelOption
                key={title}
                type={type}
                onClick={() => handleClick({ type, activityPanel: panel, url })}
                content={content}
              >
                {title}
              </UnlevelledContentActivityPanelOption>
            ),
          )}
        </ul>
      </div>
    );
  };

  const renderAttachmentsSection = () => {
    if (
      !isTeacher ||
      !filteredAttachments ||
      filteredAttachments.length === 0
    ) {
      return null;
    }

    const sortedAssessments = getSortedFormativeAssessments({
      assessments: filteredAttachments,
      expectedNames: EXPECTED_ASSESSMENT_LINKS,
    });

    return (
      <div css={panelItemWrapperCss}>
        <h3 css={sectionTitleCss}>Assessments</h3>
        <p css={sectionTextCss}>
          You can edit, assign, present, and print all Generation Genius
          assessments in Formative.
        </p>
        <ul role="group" css={activityListCss}>
          {sortedAssessments.map(({ data: { name, url } }) => (
            <UnlevelledContentActivityPanelOption
              key={name}
              type="external-link"
              onClick={() => handleClick({ type: "external-link", url })}
              content={content}
            >
              {name}
            </UnlevelledContentActivityPanelOption>
          ))}
        </ul>
        <LearnMoreFormativeLink />
      </div>
    );
  };
  const renderActivityAndAttachmentList = () => (
    <section css={sectionCss}>
      <PanelHeader
        onClickCloseButton={onClickCloseButton}
        closeButtonEventProperties={closeButtonEventProperties}
        shouldFocusCloseButtonRef={shouldFocusCloseButtonRef}
      >
        <ActivityHeader isStudent={isStudent ?? true} isArticle={isArticle} />
      </PanelHeader>
      {renderAssignmentsSection()}
      {renderActivitiesSection()}
      {renderAttachmentsSection()}
    </section>
  );

  const { page_viewmodel: pageViewmodel } = content;

  const subject =
    // fallback to SCIENCE branding if subject is undefined
    (pageViewmodel?.["subject"] as VideoSubject) || VideoSubject.SCIENCE;

  const activityPanelContent = getPanelContent(
    activityPanel,
    sharedContainerProps,
    renderActivityAndAttachmentList,
    assignments,
    assignmentsLoading,
    subject,
  );

  const getContentsWithFocusLockComponent = (isPanelVisible: boolean) => {
    // FocusOn prevents screen readers from reading hidden elements within in the activities panel when view on mobile devices.
    if (isSmallestScreen) {
      // There is a bug in FocusOn that prevents click events from being registered
      // when the panel is hidden. So we wrap panel content in FocusOn only when
      // the panel is opened, and we actually need trap the focus within the panel
      return isPanelVisible ? (
        <FocusOn scrollLock={false} css={focusContainerCss}>
          {activityPanelContent}
        </FocusOn>
      ) : (
        activityPanelContent
      );
    }
    // FocusLock maintains focus in the panel, but allows scrolling on the page when small/medium screens are used.
    return (
      <FocusLock
        disabled={!isSmallerScreen}
        returnFocus
        css={focusContainerCss}
      >
        {activityPanelContent}
      </FocusLock>
    );
  };

  return (
    <div
      data-qa-selector="activity-panel"
      data-test-id="activity-panel"
      css={wrapperCss}
    >
      <div>
        <ActivityPanelTab
          fitContent
          isArticle={isArticle}
          isStudent={isStudent}
          onClick={() => {
            setIsActivityPanelExpanded(!isActivityPanelExpanded);
          }}
        />
      </div>
      <div css={panelCss(isActivityPanelExpanded)}>
        <div css={containerCss}>
          {getContentsWithFocusLockComponent(isActivityPanelExpanded)}
        </div>
      </div>
    </div>
  );
};

export default UnlevelledContentActivityPanel;
